/**
 * FloatingBugButton - Floating bug report button with dialog
 * Allows advisors to quickly submit bug reports from anywhere in the app
 */

import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { api } from "@/shared/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
} from "@/shared/components/ui";
import { Bug, Send, AlertCircle, CheckCircle, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/shared/lib/utils";

interface TicketContext {
  pageUrl: string;
  browserInfo: string;
  screenResolution: string;
  userAgent: string;
}

function collectContext(): TicketContext {
  const getBrowserInfo = () => {
    const ua = navigator.userAgent;
    let browserName = "Unknown";
    let browserVersion = "";

    if (ua.includes("Firefox/")) {
      browserName = "Firefox";
      browserVersion = ua.split("Firefox/")[1]?.split(" ")[0] || "";
    } else if (ua.includes("Chrome/") && !ua.includes("Edg/")) {
      browserName = "Chrome";
      browserVersion = ua.split("Chrome/")[1]?.split(" ")[0] || "";
    } else if (ua.includes("Safari/") && !ua.includes("Chrome/")) {
      browserName = "Safari";
      browserVersion = ua.split("Version/")[1]?.split(" ")[0] || "";
    } else if (ua.includes("Edg/")) {
      browserName = "Edge";
      browserVersion = ua.split("Edg/")[1]?.split(" ")[0] || "";
    }

    return `${browserName} ${browserVersion}`.trim();
  };

  return {
    pageUrl: window.location.href,
    browserInfo: getBrowserInfo(),
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    userAgent: navigator.userAgent,
  };
}

export function FloatingBugButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [showContext, setShowContext] = useState(false);
  const [context, setContext] = useState<TicketContext | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const location = useLocation();

  // Update context when location changes or dialog opens
  useEffect(() => {
    if (isOpen) {
      setContext(collectContext());
    }
  }, [isOpen, location.pathname]);

  const createTicketMutation = useMutation({
    mutationFn: (data: { description: string; context: TicketContext }) =>
      api.createTicket(data),
    onSuccess: () => {
      setSubmitSuccess(true);
      setDescription("");
      setTimeout(() => {
        setIsOpen(false);
        setSubmitSuccess(false);
      }, 2000);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!context || description.trim().length < 10) return;

    createTicketMutation.mutate({
      description: description.trim(),
      context,
    });
  };

  const handleClose = () => {
    if (!createTicketMutation.isPending) {
      setIsOpen(false);
      setDescription("");
      setSubmitSuccess(false);
      setShowContext(false);
    }
  };

  const isValid = description.trim().length >= 10;

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          "fixed bottom-6 right-6 z-40",
          "w-14 h-14 rounded-full shadow-lg",
          "bg-warning-500 hover:bg-warning-600 text-white",
          "flex items-center justify-center",
          "transition-all duration-200 hover:scale-110",
          "focus:outline-none focus:ring-2 focus:ring-warning-500 focus:ring-offset-2"
        )}
        title="Zgłoś problem"
      >
        <Bug className="w-6 h-6" />
      </button>

      {/* Dialog */}
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="w-5 h-5 text-warning-500" />
              Zgłoś problem
            </DialogTitle>
            <DialogDescription>
              Opisz napotkany problem, a nasz zespół zajmie się nim jak najszybciej.
            </DialogDescription>
          </DialogHeader>

          {submitSuccess ? (
            <DialogBody className="py-8 text-center">
              <CheckCircle className="w-16 h-16 text-success-500 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-900">Dziękujemy za zgłoszenie!</p>
              <p className="text-sm text-gray-500 mt-1">
                Twoje zgłoszenie zostało przyjęte i zostanie przeanalizowane.
              </p>
            </DialogBody>
          ) : (
            <form onSubmit={handleSubmit}>
              <DialogBody className="space-y-4">
                {/* Description */}
                <div>
                  <label
                    htmlFor="bug-description"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Opis problemu <span className="text-error-500">*</span>
                  </label>
                  <textarea
                    id="bug-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Opisz szczegółowo co się wydarzyło, jakich kroków użyłeś i czego oczekiwałeś..."
                    rows={5}
                    className={cn(
                      "w-full px-3 py-2 border rounded-lg text-sm",
                      "focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500",
                      "placeholder:text-gray-400",
                      description.length > 0 && description.length < 10
                        ? "border-error-300"
                        : "border-gray-300"
                    )}
                    required
                    minLength={10}
                    maxLength={5000}
                  />
                  <div className="flex justify-between mt-1">
                    <span
                      className={cn(
                        "text-xs",
                        description.length > 0 && description.length < 10
                          ? "text-error-500"
                          : "text-gray-400"
                      )}
                    >
                      {description.length < 10
                        ? `Minimum 10 znaków (${description.length}/10)`
                        : ""}
                    </span>
                    <span className="text-xs text-gray-400">{description.length}/5000</span>
                  </div>
                </div>

                {/* Auto-collected context */}
                <div className="bg-gray-50 rounded-lg border border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowContext(!showContext)}
                    className="w-full px-3 py-2 flex items-center justify-between text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Automatycznie zebrane informacje
                    </span>
                    {showContext ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>

                  {showContext && context && (
                    <div className="px-3 pb-3 space-y-1.5 text-xs text-gray-500 border-t border-gray-200 pt-2">
                      <div>
                        <span className="font-medium">Strona:</span>{" "}
                        <span className="break-all">{context.pageUrl}</span>
                      </div>
                      <div>
                        <span className="font-medium">Przeglądarka:</span> {context.browserInfo}
                      </div>
                      <div>
                        <span className="font-medium">Rozdzielczość:</span> {context.screenResolution}
                      </div>
                    </div>
                  )}
                </div>

                {/* Error */}
                {createTicketMutation.error && (
                  <div className="p-3 bg-error-50 border border-error-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0" />
                    <p className="text-sm text-error-700">
                      {(createTicketMutation.error as Error).message ||
                        "Wystąpił błąd podczas wysyłania zgłoszenia. Spróbuj ponownie."}
                    </p>
                  </div>
                )}
              </DialogBody>

              <DialogFooter>
                <Button type="button" variant="secondary" onClick={handleClose}>
                  Anuluj
                </Button>
                <Button
                  type="submit"
                  disabled={!isValid}
                  isLoading={createTicketMutation.isPending}
                  leftIcon={<Send className="w-4 h-4" />}
                >
                  Wyślij zgłoszenie
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
